import { ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTikTokShopAuthUrl } from "@/api/tiktok-shop";
import { Button } from "@/components/base/buttons/button";
import { useMerchant } from "@/providers/MerchantProvider";

interface TikTokShopConnectProps {
  onSuccess?: () => void;
}

export const TikTokShopConnect = ({ onSuccess }: TikTokShopConnectProps) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const merchant = useMerchant();

  const { data: authData, isLoading } = useTikTokShopAuthUrl();
  const authUrl = authData?.authUrl;

  // Check if we just returned from OAuth (success case)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "success") {
      onSuccess?.();
    }
  }, [onSuccess]);

  const handleConnect = () => {
    if (!merchant) {
      alert("No merchant selected. Please select a merchant first.");
      return;
    }

    if (!authUrl) {
      alert("TikTok Shop is not configured for your region. Please contact support.");
      return;
    }

    setIsRedirecting(true);
    window.location.href = authUrl;
  };

  // Show success message if returning from OAuth
  const params = new URLSearchParams(window.location.search);
  if (params.get("status") === "success") {
    return (
      <div className="flex flex-col items-center space-y-4 text-center py-8">
        <div className="text-green-500 text-6xl">✓</div>
        <h2 className="text-2xl font-semibold text-gray-900">Successfully Connected!</h2>
        <p className="text-gray-600">Your TikTok Shop account has been connected successfully.</p>
      </div>
    );
  }

  // Show loading state while fetching the authorization URL
  if (isLoading) {
    return (
      <div className="flex flex-col items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <p className="text-sm text-gray-600 mt-4">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Connect Your TikTok Shop</h2>
        <p className="text-gray-600">Connect your TikTok Shop account to continue.</p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-sm text-gray-900 mb-2">What happens next?</h3>
        <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
          <li>You'll be redirected to TikTok Shop</li>
          <li>Log in with your seller account</li>
          <li>Review and approve the permissions</li>
          <li>You'll be redirected back automatically</li>
        </ol>
      </div>

      <Button
        color="primary"
        size="lg"
        onClick={handleConnect}
        className="w-full"
        isDisabled={isRedirecting || !authUrl}
        iconLeading={isRedirecting ? Loader2 : ExternalLink}
      >
        {isRedirecting ? "Redirecting..." : "Connect TikTok Shop"}
      </Button>
    </div>
  );
};
